import { Action } from '@ngrx/store';

import {
  Crop,
  Template,
  SalesOrderPeriod,
  CopyTemplatesByYearRequest,
  PaginationState,
  TemplateFilter,
  NutrientProperties,
  UpsertNutrientPropertiesRequest,
  SoilType,
  UnitOfMeasure,
  NutrientDesignation,
  NutrientSource,
  VarietyProperties,
  UpsertVarietyPropertiesRequest,
  CropVariety,
  ProductionPurposeCategory,
  UpsertNitrogenModelPropertiesRequest,
  NitrogenModelProperties,
  ProductionPurposeCategoryDetailed,
  UpsertProductionPurposeCategoryRequest,
} from '../shared/models';

export enum AppActionTypes {
  GetTemplates = '[App] Get templates',
  SetTemplates = '[App] Set templates',
  SetTemplate = '[App] Set template',
  GetCrops = '[App] Get crop',
  SetCrops = '[App] Set crop',
  ResetCropYear = '[App] Reset crop year',
  SetCropYear = '[App] Set crop year',
  SetDefaultCropYear = '[App] Set default crop year',
  SetAvailableCropYears = '[App] Set available crop year',
  SetCropYearInStore = '[App] Set crop year in store',
  OpenCreateTemplateDialog = '[App] Open create template dialog',
  CreateTemplate = '[App] Create template',
  FetchTemplatesAndRedirect = '[App] Fetch templates and redirect to template',
  RedirectToTemplate = '[App] Redirect to template',
  RemoveTemplate = '[App] Remove template',
  GetSalesOrderPeriods = '[App] Get sales order periods',
  SetSalesOrderPeriods = '[App] Set sales order periods',
  OpenCopyTemplatesByYearDialog = '[App] Open copy templates by year dialog',
  CopyTemplatesByYear = '[App] Copy templates by year',
  UpdateTemplateFilter = '[App] Update template filter',
  ClearTemplateFilter = '[App] Clear template filter',
  GetNutrientProperties = '[App] Get nutrient properties',
  SetNutrientProperties = '[App] Set nutrient properties',
  UpsertNutrientProperties = '[App] Upsert nutrient properties',
  DeleteNutrientProperties = '[App] Delete nutrient properties',
  GetSoilTypes = '[App] Get soil types',
  SetSoilTypes = '[App] Set soil types',
  GetUnitsOfMeasure = '[App] Get units of measure',
  SetUnitsOfMeasure = '[App] Set units of measure',
  OpenCreateNutrientPropertyDialog = '[App] Open create nutrient property dialog',
  GetNutrientDesignations = '[App] Get nutrient designations',
  SetNutrientDesignations = '[App] Set nutrient designations',
  GetNutrientSources = '[App] Get nutrient sources',
  SetNutrientSources = '[App] Set nutrient sources',
  OpenEditNutrientPropertyDialog = '[App] Open edit nutrient property dialog',
  GetVarietyProperties = '[App] Get variety properties',
  SetVarietyProperties = '[App] Set variety properties',
  UpsertVarietyProperties = '[App] Upsert variety properties',
  DeleteVarietyProperties = '[App] Delete variety properties',
  OpenCreateVarietyPropertyDialog = '[App] Open create variety property dialog',
  OpenEditVarietyPropertyDialog = '[App] Open edit variety property dialog',
  GetCropVarieties = '[App] Get crop varieties',
  SetCropVarieties = '[App] Set crop varieties',
  GetProductionPurposeCategories = '[App] Get production purpose categories',
  GetNitrogenModelProperties = '[App] Get nitrogen model properties',
  SetNitrogenModelProperties = '[App] Set nitrogen model properties',
  UpsertNitrogenModelProperties = '[App] Upsert nitrogen model properties',
  DeleteNitrogenModelProperties = '[App] Delete nitrogen model properties',
  OpenCreateNitrogenModelPropertyDialog = '[App] Open create nitrogen model property dialog',
  OpenEditNitrogenModelPropertyDialog = '[App] Open edit nitrogen model property dialog',
  GetProductionPurposeCategoriesDetailed = '[App] Get production purpose categories detailed',
  OpenCreateProductionPurposeCategoriesDialog = '[App] Open create production purpose categories dialog',
  OpenEditProductionPurposeCategoryDialog = '[App] Open edit production purpose category dialog',
  DeleteProductionPurposeCategory = '[App] Delete production purpose category',
  SetProductionPurposeCategoriesDetailed = '[App] Set production purpose categories detailed',
  SetProductionPurposeCategories = '[App] Set production purpose categories',
  UpsertProductionPurposeCategoriesDetailed = '[App] Upsert variety properties detailed',
}

export class GetTemplates implements Action {
  readonly type = AppActionTypes.GetTemplates;

  constructor(
    public readonly pageNumber?: number,
    public readonly pageSize?: number,
  ) {}
}

export class SetTemplates implements Action {
  readonly type = AppActionTypes.SetTemplates;
  constructor(
    public readonly templates: Template[],
    public readonly pagination: PaginationState,
  ) {}
}

export class SetTemplate implements Action {
  readonly type = AppActionTypes.SetTemplate;
  constructor(public readonly template: Template) {}
}

export class GetCrops implements Action {
  readonly type = AppActionTypes.GetCrops;
}

export class SetCrops implements Action {
  readonly type = AppActionTypes.SetCrops;
  constructor(public readonly cropVarieties: Crop[]) {}
}

export class ResetCropYear implements Action {
  readonly type = AppActionTypes.ResetCropYear;
  constructor(public readonly cropYear?: number) {}
}

export class SetCropYear implements Action {
  readonly type = AppActionTypes.SetCropYear;
  constructor(public readonly cropYear: number) {}
}

export class SetDefaultCropYear implements Action {
  readonly type = AppActionTypes.SetDefaultCropYear;
  constructor(public readonly cropYear: number) {}
}

export class SetAvailableCropYears implements Action {
  readonly type = AppActionTypes.SetAvailableCropYears;
  constructor(public readonly availableCropYears: number[]) {}
}

export class SetCropYearInStore implements Action {
  readonly type = AppActionTypes.SetCropYearInStore;
  constructor(public readonly cropYear: number) {}
}

export class OpenCreateTemplateDialog implements Action {
  readonly type = AppActionTypes.OpenCreateTemplateDialog;
}

export class CreateTemplate implements Action {
  readonly type = AppActionTypes.CreateTemplate;
  constructor(public readonly template: Template) {}
}

export class FetchTemplatesAndRedirect implements Action {
  readonly type = AppActionTypes.FetchTemplatesAndRedirect;
  constructor(public readonly id: number) {}
}

export class RedirectToTemplate implements Action {
  readonly type = AppActionTypes.RedirectToTemplate;
  constructor(
    public readonly id: number,
    public readonly cropYear: number,
  ) {}
}

export class RemoveTemplate implements Action {
  readonly type = AppActionTypes.RemoveTemplate;
  constructor(public readonly id: number) {}
}

export class GetSalesOrderPeriods implements Action {
  readonly type = AppActionTypes.GetSalesOrderPeriods;
  constructor() {}
}

export class SetSalesOrderPeriods implements Action {
  readonly type = AppActionTypes.SetSalesOrderPeriods;
  constructor(public readonly salesOrderPeriods: SalesOrderPeriod[]) {}
}

export class OpenCopyTemplatesByYearDialog implements Action {
  readonly type = AppActionTypes.OpenCopyTemplatesByYearDialog;
}

export class CopyTemplatesByYear implements Action {
  readonly type = AppActionTypes.CopyTemplatesByYear;
  constructor(public readonly request: CopyTemplatesByYearRequest) {}
}

export class UpdateTemplateFilter implements Action {
  readonly type = AppActionTypes.UpdateTemplateFilter;
  constructor(public readonly templateFilter: TemplateFilter) {}
}

export class ClearTemplateFilter implements Action {
  readonly type = AppActionTypes.ClearTemplateFilter;
}
export class GetNutrientProperties implements Action {
  readonly type = AppActionTypes.GetNutrientProperties;
}

export class SetNutrientProperties implements Action {
  readonly type = AppActionTypes.SetNutrientProperties;
  constructor(public readonly nutrientProperties: NutrientProperties[]) {}
}

export class UpsertNutrientProperties implements Action {
  readonly type = AppActionTypes.UpsertNutrientProperties;
  constructor(public readonly request: UpsertNutrientPropertiesRequest) {}
}

export class DeleteNutrientProperties implements Action {
  readonly type = AppActionTypes.DeleteNutrientProperties;
  constructor(public readonly id: number) {}
}

export class GetSoilTypes implements Action {
  readonly type = AppActionTypes.GetSoilTypes;
}

export class SetSoilTypes implements Action {
  readonly type = AppActionTypes.SetSoilTypes;
  constructor(public readonly soilTypes: SoilType[]) {}
}

export class GetUnitsOfMeasure implements Action {
  readonly type = AppActionTypes.GetUnitsOfMeasure;
}

export class SetUnitsOfMeasure implements Action {
  readonly type = AppActionTypes.SetUnitsOfMeasure;
  constructor(public readonly unitsOfMeasure: UnitOfMeasure[]) {}
}

export class OpenCreateNutrientPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenCreateNutrientPropertyDialog;
}

export class GetNutrientDesignations implements Action {
  readonly type = AppActionTypes.GetNutrientDesignations;
}

export class SetNutrientDesignations implements Action {
  readonly type = AppActionTypes.SetNutrientDesignations;
  constructor(public readonly nutrientDesignations: NutrientDesignation[]) {}
}

export class GetNutrientSources implements Action {
  readonly type = AppActionTypes.GetNutrientSources;
}

export class SetNutrientSources implements Action {
  readonly type = AppActionTypes.SetNutrientSources;
  constructor(public readonly nutrientSources: NutrientSource[]) {}
}

export class OpenEditNutrientPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenEditNutrientPropertyDialog;
  constructor(public payload: { nutrientProperty: NutrientProperties }) {}
}

export class GetVarietyProperties implements Action {
  readonly type = AppActionTypes.GetVarietyProperties;
  constructor(public readonly ediCode?: number) {}
}

export class SetVarietyProperties implements Action {
  readonly type = AppActionTypes.SetVarietyProperties;
  constructor(public readonly varietyProperties: VarietyProperties[]) {}
}

export class UpsertVarietyProperties implements Action {
  readonly type = AppActionTypes.UpsertVarietyProperties;
  constructor(public readonly request: UpsertVarietyPropertiesRequest) {}
}

export class DeleteVarietyProperties implements Action {
  readonly type = AppActionTypes.DeleteVarietyProperties;
  constructor(public readonly id: number) {}
}

export class OpenCreateVarietyPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenCreateVarietyPropertyDialog;
}

export class OpenEditVarietyPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenEditVarietyPropertyDialog;
  constructor(public payload: { varietyProperty: VarietyProperties; existingVarietyProperties?: VarietyProperties[] }) {}
}

export class GetCropVarieties implements Action {
  readonly type = AppActionTypes.GetCropVarieties;
}

export class GetProductionPurposeCategories implements Action {
  readonly type = AppActionTypes.GetProductionPurposeCategories;
}
export class SetProductionPurposeCategoryDetailed implements Action {
  readonly type = AppActionTypes.SetProductionPurposeCategoriesDetailed;
  constructor(
    public readonly productionPurposeCategoryDetailed: ProductionPurposeCategoryDetailed[],
  ) {}
}
export class GetProductionPurposeCategoriesDetailed implements Action {
  readonly type = AppActionTypes.GetProductionPurposeCategoriesDetailed;
}
export class OpenCreateProductionPurposeCategoriesDialog implements Action {
  readonly type = AppActionTypes.OpenCreateProductionPurposeCategoriesDialog;
}
export class OpenEditProductionPurposeCategoryDialog implements Action {
  readonly type = AppActionTypes.OpenEditProductionPurposeCategoryDialog;
  constructor(public payload: { productionPurposeCategory: ProductionPurposeCategoryDetailed }) {}
}
export class DeleteProductionPurposeCategory implements Action {
  readonly type = AppActionTypes.DeleteProductionPurposeCategory;
  constructor(public readonly id: number) {}
}
export class UpsertProductionPurposeCategory implements Action {
  readonly type = AppActionTypes.UpsertProductionPurposeCategoriesDetailed;
  constructor(public readonly request: UpsertProductionPurposeCategoryRequest) {}
}
export class SetCropVarieties implements Action {
  readonly type = AppActionTypes.SetCropVarieties;
  constructor(public readonly cropVarieties: CropVariety[]) {}
}

export class SetProductionPurposeCategories implements Action {
  readonly type = AppActionTypes.SetProductionPurposeCategories;
  constructor(public readonly productionPurposeCategories: ProductionPurposeCategory[]) {}
}

export class GetNitrogenModelProperties implements Action {
  readonly type = AppActionTypes.GetNitrogenModelProperties;
}

export class SetNitrogenModelProperties implements Action {
  readonly type = AppActionTypes.SetNitrogenModelProperties;
  constructor(public readonly nitrogenModelProperties: NitrogenModelProperties[]) {}
}

export class UpsertNitrogenModelProperties implements Action {
  readonly type = AppActionTypes.UpsertNitrogenModelProperties;
  constructor(public readonly request: UpsertNitrogenModelPropertiesRequest) {}
}

export class DeleteNitrogenModelProperties implements Action {
  readonly type = AppActionTypes.DeleteNitrogenModelProperties;
  constructor(public readonly id: number) {}
}

export class OpenCreateNitrogenModelPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenCreateNitrogenModelPropertyDialog;
}

export class OpenEditNitrogenModelPropertyDialog implements Action {
  readonly type = AppActionTypes.OpenEditNitrogenModelPropertyDialog;
  constructor(public payload: { nitrogenModelProperties: NitrogenModelProperties }) {}
}

// export the combined type of all actions
export type TemplateActions =
  | GetTemplates
  | SetTemplates
  | SetTemplate
  | GetCrops
  | SetCrops
  | ResetCropYear
  | SetCropYear
  | SetDefaultCropYear
  | SetAvailableCropYears
  | SetCropYearInStore
  | OpenCreateTemplateDialog
  | CreateTemplate
  | FetchTemplatesAndRedirect
  | RedirectToTemplate
  | RemoveTemplate
  | GetSalesOrderPeriods
  | SetSalesOrderPeriods
  | OpenCopyTemplatesByYearDialog
  | CopyTemplatesByYear
  | UpdateTemplateFilter
  | ClearTemplateFilter
  | GetNutrientProperties
  | SetNutrientProperties
  | UpsertNutrientProperties
  | DeleteNutrientProperties
  | GetSoilTypes
  | SetSoilTypes
  | GetUnitsOfMeasure
  | SetUnitsOfMeasure
  | OpenCreateNutrientPropertyDialog
  | GetNutrientDesignations
  | SetNutrientDesignations
  | SetNutrientSources
  | GetNutrientSources
  | OpenEditNutrientPropertyDialog
  | GetVarietyProperties
  | SetVarietyProperties
  | UpsertVarietyProperties
  | DeleteVarietyProperties
  | OpenCreateVarietyPropertyDialog
  | OpenEditVarietyPropertyDialog
  | GetCropVarieties
  | SetCropVarieties
  | GetProductionPurposeCategories
  | SetProductionPurposeCategories
  | GetNitrogenModelProperties
  | SetNitrogenModelProperties
  | UpsertNitrogenModelProperties
  | DeleteNitrogenModelProperties
  | OpenCreateNitrogenModelPropertyDialog
  | OpenEditNitrogenModelPropertyDialog
  | GetProductionPurposeCategoriesDetailed
  | OpenCreateProductionPurposeCategoriesDialog
  | OpenEditProductionPurposeCategoryDialog
  | DeleteProductionPurposeCategory
  | SetProductionPurposeCategoryDetailed
  | UpsertProductionPurposeCategory;
